<template>
  <teleport to="body">
    <div v-if="open" class="cmd-overlay" @click.self="$emit('close')">
      <div class="cmd-dialog m-float">
        <div class="cmd-header">
          <svg class="cmd-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            ref="inputRef"
            v-model="keyword"
            class="cmd-input"
            placeholder="搜索页面、常用指令或输入关键词... (Esc 退出)"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="selectCurrent"
            @keydown.esc.prevent="$emit('close')"
          />
          <span class="cmd-esc-tip">ESC</span>
        </div>

        <div class="cmd-body" ref="listRef">
          <div v-if="filteredEntries.length === 0" class="cmd-empty">
            未找到匹配项
          </div>
          <div
            v-for="(group, gIdx) in groupedEntries"
            :key="group.name"
            class="cmd-group"
          >
            <div class="cmd-group-title">{{ group.name }}</div>
            <div
              v-for="item in group.items"
              :key="item.key"
              :class="['cmd-item', { 'is-active': item.globalIndex === activeIndex }]"
              @click="runItem(item)"
              @mouseenter="activeIndex = item.globalIndex"
            >
              <div class="cmd-item-icon">
                <span v-if="item.icon && item.icon.startsWith('icon-')" :class="['iconfont', item.icon]"></span>
                <svg v-else-if="item.type === 'action'" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
              </div>
              <div class="cmd-item-info">
                <span class="cmd-item-title">{{ item.title }}</span>
                <span v-if="item.sub" class="cmd-item-sub">{{ item.sub }}</span>
              </div>
              <span class="cmd-item-enter">↵</span>
            </div>
          </div>
        </div>

        <div class="cmd-footer">
          <span><kbd>↑</kbd> <kbd>↓</kbd> 导航</span>
          <span><kbd>↵</kbd> 跳转 / 执行</span>
          <span><kbd>ESC</kbd> 关闭</span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/store'

interface CommandEntry {
  key: string
  group: string
  title: string
  sub?: string
  icon?: string
  type?: 'page' | 'action'
  run: () => void
}

export default defineComponent({
  name: 'CommandPalette',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'action'],
  setup(props, { emit }) {
    const router = useRouter()
    const store = useStore()
    const keyword = ref('')
    const activeIndex = ref(0)
    const inputRef = ref<HTMLInputElement | null>(null)
    const listRef = ref<HTMLElement | null>(null)

    // 聚焦输入框
    watch(() => props.open, (val) => {
      if (val) {
        keyword.value = ''
        activeIndex.value = 0
        nextTick(() => inputRef.value?.focus())
      }
    })

    // 展平所有可访问路由
    const pageEntries = computed<CommandEntry[]>(() => {
      const routes = store.state.permission.routes || []
      const list: CommandEntry[] = []

      function walk(items: any[], parentTitle = '') {
        for (const item of items) {
          if (item.meta?.hidden) continue
          const currentTitle = item.meta?.title || item.name || ''
          const fullTitle = parentTitle ? `${parentTitle} › ${currentTitle}` : currentTitle

          if (item.children && item.children.length > 0) {
            walk(item.children, fullTitle)
          } else if (item.path && currentTitle) {
            list.push({
              key: `route-${item.path}`,
              group: '页面导航',
              title: currentTitle,
              sub: fullTitle !== currentTitle ? fullTitle : item.path,
              icon: item.meta?.icon,
              type: 'page',
              run: () => {
                emit('close')
                router.push(item.path)
              }
            })
          }
        }
      }

      walk(routes)
      return list
    })

    // 快捷系统操作
    const actionEntries = computed<CommandEntry[]>(() => {
      const isDark = document.documentElement.dataset.theme === 'dark'
      return [
        {
          key: 'action-theme',
          group: '系统操作',
          title: isDark ? '切换到浅色珍珠主题' : '切换到深色夜间主题',
          sub: '切换全站视觉外观',
          type: 'action',
          run: () => {
            const next = isDark ? 'light' : 'dark'
            document.documentElement.dataset.theme = next
            localStorage.setItem('erp.theme', next)
            emit('close')
          }
        },
        {
          key: 'action-dashboard',
          group: '快捷入口',
          title: '进入业务仪表盘 (Dashboard)',
          sub: '查看四维业务雷达与核心指标',
          type: 'action',
          run: () => {
            emit('close')
            router.push('/dashboard')
          }
        },
        {
          key: 'action-pwd',
          group: '系统操作',
          title: '修改个人密码',
          sub: '更新账号安全凭证',
          type: 'action',
          run: () => {
            emit('close')
            emit('action', 'change-password')
          }
        },
        {
          key: 'action-logout',
          group: '系统操作',
          title: '退出登录',
          sub: '清除凭据并返回登录界面',
          type: 'action',
          run: () => {
            emit('close')
            emit('action', 'logout')
          }
        }
      ]
    })

    const allEntries = computed(() => [...actionEntries.value, ...pageEntries.value])

    const filteredEntries = computed(() => {
      const kw = keyword.value.trim().toLowerCase()
      if (!kw) return allEntries.value
      return allEntries.value.filter(e =>
        e.title.toLowerCase().includes(kw) ||
        (e.sub && e.sub.toLowerCase().includes(kw))
      )
    })

    const groupedEntries = computed(() => {
      const groups: { name: string; items: Array<CommandEntry & { globalIndex: number }> }[] = []
      let idx = 0
      for (const entry of filteredEntries.value) {
        let g = groups.find(x => x.name === entry.group)
        if (!g) {
          g = { name: entry.group, items: [] }
          groups.push(g)
        }
        g.items.push({ ...entry, globalIndex: idx++ })
      }
      return groups
    })

    function move(dir: number) {
      const len = filteredEntries.value.length
      if (len === 0) return
      activeIndex.value = (activeIndex.value + dir + len) % len
    }

    function selectCurrent() {
      const item = filteredEntries.value[activeIndex.value]
      if (item) runItem(item)
    }

    function runItem(item: CommandEntry) {
      item.run()
    }

    return {
      keyword,
      activeIndex,
      inputRef,
      listRef,
      filteredEntries,
      groupedEntries,
      move,
      selectCurrent,
      runItem
    }
  }
})
</script>

<style scoped lang="scss">
.cmd-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000 !important;
  background: var(--overlay);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 14vh;
}

.cmd-dialog {
  width: 580px;
  max-width: 90vw;
  border-radius: var(--r-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: cmd-pop 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes cmd-pop {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.cmd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
}

.cmd-icon {
  width: 20px;
  height: 20px;
  color: var(--ink-soft);
  flex-shrink: 0;
}

.cmd-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 15px;
  color: var(--ink);
  &::placeholder {
    color: var(--ink-faint);
  }
}

.cmd-esc-tip {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--groove-bg);
  color: var(--ink-soft);
  font-family: var(--font-mono);
}

.cmd-body {
  max-height: 380px;
  overflow-y: auto;
  padding: 10px 12px;
}

.cmd-empty {
  padding: 32px;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
}

.cmd-group-title {
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-faint);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.cmd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: all 0.15s ease;
  color: var(--ink-muted);

  &.is-active {
    background: var(--pill-bg);
    box-shadow: var(--e1);
    color: var(--ink);
    .cmd-item-icon {
      color: var(--blue);
    }
    .cmd-item-enter {
      opacity: 1;
    }
  }
}

.cmd-item-icon {
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-soft);
}

.cmd-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.cmd-item-title {
  font-size: 13.5px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cmd-item-sub {
  font-size: 11.5px;
  color: var(--ink-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cmd-item-enter {
  opacity: 0;
  font-size: 12px;
  color: var(--ink-soft);
  font-family: var(--font-mono);
  transition: opacity 0.15s ease;
}

.cmd-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 20px;
  border-top: 1px solid var(--line);
  background: rgba(var(--shade), 0.02);
  font-size: 11px;
  color: var(--ink-faint);

  kbd {
    padding: 2px 5px;
    border-radius: 4px;
    background: var(--groove-bg);
    color: var(--ink-soft);
    font-family: var(--font-mono);
  }
}
</style>
